import { Component, EventEmitter, Input, Output } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ZkNotificationService } from '@zenklub/ng-ui';

import {
	EBillingFileType,
	EBillingType,
	IBillingFile,
	IProfessionalPerson,
	IProfessionalProfile,
} from '../../../interfaces/professionalprofile.interface';
import { UserService } from '../../../services/user.service';

export interface ZKUploadItem {
	title: string;
	description: string;
	labelSelectFile: string;
	errorMessage: string;
	labelChangeFile: string;
	uploadStatus: 'undefined' | 'success' | 'error' | 'disabled';
	filesUploaded?: string[];
	dialog: {
		showIcon: boolean;
		labelCancel: string;
		labelNext: string;
		labelSelectFile: string;
		description?: string;
		uploading: boolean;
		isOpen: boolean;
	};
	required?: boolean;
	type: EBillingFileType;
}

@Component({
	selector: 'app-billing-documents',
	templateUrl: './billing-documents.component.html',
	styleUrls: ['./billing-documents.component.scss'],
})
export class BillingDocumentsComponent {
	@Input() user: IProfessionalProfile;
	@Input() professionalPerson: IProfessionalPerson;
	@Input() disabled: boolean;
	@Input() showRequiredFiles: boolean;
	@Input() billingType: EBillingType;
	@Output() fileSaved: EventEmitter<void> = new EventEmitter<void>();
	filetypes = EBillingFileType;
	legalDocuments: ZKUploadItem[];
	naturalDocuments: ZKUploadItem[];
	constructor(
		public notifications: ZkNotificationService,
		private translateService: TranslateService,
		public userService: UserService
	) {}

	ngOnInit() {
		this.buildDocumentsList();
		this.syncDocumentsInProfile();
	}

	uploadFile(fileEvent: string[], uploadItem: ZKUploadItem) {
		const translatePath = 'MY_ACCOUNT.FINANCIAL.BILLING_DOCUMENTS';
		if (!this.fileSizeIsValid(fileEvent[0])) {
			this.notifications.showError(
				this.translateService.instant(translatePath + '.FILE_SIZE_EXCEEDED')
			);
			return;
		}
		if (!this.fileTypeIsValid(fileEvent[0])) {
			this.notifications.showError(
				this.translateService.instant(translatePath + '.FILE_TYPE_INVALID')
			);
			return;
		}
		uploadItem.dialog.uploading = true;
		uploadItem.dialog.isOpen = true;
		uploadItem.uploadStatus = 'undefined';
		const data: IBillingFile = {
			documentType: uploadItem.type,
			personType: this.billingType,
			filesInBase64: fileEvent.map((fileItem) => fileItem.split('data:')[1]),
		};
		this.userService.uploadFinancialFiles(data).subscribe((response) => {
			this.addFileUploadedInProfile(uploadItem.type, response.urls);
			uploadItem.dialog.uploading = false;
			uploadItem.uploadStatus = 'success';
			uploadItem.dialog.isOpen = false;
			this.fileSaved.emit();
			this.notifications.showSuccess(
				this.translateService.instant(translatePath + '.SAVED')
			);
		});
	}

	fileTypeIsValid(fileString: string): boolean {
		const mime = fileString.substring(
			'data:'.length,
			fileString.indexOf(';base64')
		);
		if (!mime) {
			return false;
		}
		return mime.startsWith('image') || mime.startsWith('application/pdf');
	}

	getDocumentStatus(document: ZKUploadItem) {
		const { uploadStatus, required } = document;
		if (this.disabled) {
			return 'disabled';
		}
		if (required && this.showRequiredFiles && uploadStatus !== 'success') {
			return 'error';
		}
		return uploadStatus;
	}

	buildDocumentsList() {
		const { ID, PIS, Address, WorkCard, CNPJ_Card } = this.filetypes;
		this.naturalDocuments = [];
		[ID, PIS, Address, WorkCard].forEach((fileType) => {
			this.naturalDocuments.push(this.getUploadItem(fileType));
		});
		this.legalDocuments = [this.getUploadItem(CNPJ_Card)];
	}

	addFileUploadedInProfile(fileType: EBillingFileType, filesUrl: string[]) {
		const legalPerson = this.user.legalPerson;
		const naturalPerson = this.user.naturalPerson;
		if (this.billingType == EBillingType.Legal) {
			legalPerson.documents = legalPerson.documents.filter(
				(document) => document.documentType !== fileType
			);
			filesUrl.forEach((fileUrl) => {
				legalPerson.documents.push({ documentType: fileType, url: fileUrl });
			});
		} else {
			naturalPerson.documents = naturalPerson.documents.filter(
				(document) => document.documentType !== fileType
			);
			filesUrl.forEach((fileUrl) => {
				naturalPerson.documents.push({ documentType: fileType, url: fileUrl });
			});
		}
	}

	getDocumentsSource() {
		const { type, professionalData } = this.professionalPerson;
		if (this.billingType == type && professionalData) {
			return professionalData;
		}
		return this.user;
	}

	syncDocumentsInProfile() {
		const hasDocument = (documents, document) => {
			return documents.find(
				(item) => item.documentType === document.documentType
			);
		};
		const { professionalData } = this.professionalPerson;
		if (!professionalData) {
			return;
		}
		const naturalDocuments = professionalData.naturalPerson?.documents || [];
		const legalDocuments = professionalData.legalPerson?.documents || [];
		const userNaturalDocuments = this.user.naturalPerson.documents;
		const userLegalDocuments = this.user.legalPerson.documents;
		naturalDocuments.forEach((doc) => {
			if (!hasDocument(userNaturalDocuments, doc)) {
				this.user.naturalPerson.documents.push(doc);
			}
		});
		legalDocuments.forEach((doc) => {
			if (!hasDocument(userLegalDocuments, doc)) {
				this.user.legalPerson.documents.push(doc);
			}
		});
	}

	private fileSizeIsValid(fileBase64: string) {
		const maxFileSize = 3;
		const byteLength = fileBase64.length * (3 / 4) - 2;
		return byteLength / 1e6 <= maxFileSize;
	}

	private getUploadItem(fileType: EBillingFileType): ZKUploadItem {
		const translatePath = 'MY_ACCOUNT.FINANCIAL.BILLING_DOCUMENTS';
		const { ID, WorkCard } = this.filetypes;
		const document: ZKUploadItem = {
			title: `${translatePath}.${fileType}.TITLE`,
			type: fileType,
			description: `${translatePath}.${fileType}.DESCRIPTION`,
			errorMessage: `${translatePath}.${fileType}.ERROR`,
			labelChangeFile: `${translatePath}.LABEL_CHANGE_FILE`,
			labelSelectFile: `${translatePath}.LABEL_SELECT_FILE`,
			uploadStatus: 'undefined',
			required: fileType !== EBillingFileType.WorkCard,
			dialog: {
				labelCancel: `${translatePath}.LABEL_CANCEL`,
				labelNext: `${translatePath}.LABEL_NEXT`,
				labelSelectFile: `${translatePath}.LABEL_DIALOG_SELECT_FILE`,
				showIcon: true,
				uploading: false,
				isOpen: false,
			},
		};
		if ([ID, WorkCard].includes(fileType)) {
			document.dialog.description = `${translatePath}.${fileType}.DIALOG_DESCRIPTION`;
		}
		const documentsFile = this.getFilesUploadedByType(fileType);
		if (documentsFile.length) {
			document.filesUploaded = [];
			documentsFile.forEach((documentFile) => {
				document.filesUploaded.push(documentFile.url.split('/').pop());
			});
			document.uploadStatus = 'success';
		}
		return document;
	}

	private getFilesUploadedByType(fileType: EBillingFileType) {
		const source = this.getDocumentsSource();
		const { CNPJ_Card } = EBillingFileType;
		let documents = source.naturalPerson?.documents || [];
		if (fileType == CNPJ_Card && source.legalPerson?.documents) {
			documents = source.legalPerson.documents;
		}
		return documents.filter((document) => document.documentType == fileType);
	}
}
